import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { PhotosByEvent, SeasonContext } from '../../../../types';
import { useCarouselData } from '../../../../hooks/useCarouselData';
import AvatarCarouselPresentation from './AvatarCarouselPresentation';

// Subpixel scroll positions never land exactly on either end.
const SCROLL_EDGE_TOLERANCE_PX = 1;
// The focus target inside each AvatarBubble.
const BUBBLE_FOCUS_SELECTOR = '.avatar-bubble-inner';
const BUBBLE_STEP_BY_KEY: Record<string, -1 | 1> = { ArrowLeft: -1, ArrowRight: 1 };

interface ScrollEdges {
  canGoPrev: boolean;
  canGoNext: boolean;
}

const NO_SCROLL_EDGES: ScrollEdges = { canGoPrev: false, canGoNext: false };

function readScrollEdges(track: HTMLElement): ScrollEdges {
  const maxScrollLeft = track.scrollWidth - track.clientWidth;
  return {
    canGoPrev: track.scrollLeft > SCROLL_EDGE_TOLERANCE_PX,
    canGoNext: track.scrollLeft < maxScrollLeft - SCROLL_EDGE_TOLERANCE_PX,
  };
}

interface AvatarCarouselContainerProps {
  onAvatarClick: (event: string | null) => void;
  openEvent: string | null;
  seasonEvents: PhotosByEvent;
  seasonContext?: SeasonContext;
}

/**
 * The track is a native horizontal scroller with CSS scroll-snap: the arrows scroll it by
 * one visible width, and focus scrolls it natively, so no bubble geometry is computed here.
 * Left/Right between bubbles is handled here rather than by the TV's spatial navigation,
 * which reaches an off-screen bubble only after several small scroll steps.
 */
const AvatarCarouselContainer: React.FC<AvatarCarouselContainerProps> = ({
  onAvatarClick,
  openEvent,
  seasonEvents,
  seasonContext
}) => {
  const { sortedEvents } = useCarouselData(seasonEvents);
  const trackRef = useRef<HTMLDivElement>(null);
  const [scrollEdges, setScrollEdges] = useState<ScrollEdges>(NO_SCROLL_EDGES);

  const syncScrollEdges = useCallback(() => {
    const track = trackRef.current;
    if (track) {
      setScrollEdges(readScrollEdges(track));
    }
  }, []);

  useLayoutEffect(() => {
    const track = trackRef.current;
    if (!track) {
      return;
    }

    syncScrollEdges();
    const observer = new ResizeObserver(syncScrollEdges);
    observer.observe(track);

    return () => observer.disconnect();
  }, [sortedEvents.length, syncScrollEdges]);

  const scrollByVisibleWidth = (direction: -1 | 1) => {
    const track = trackRef.current;
    if (!track) {
      return;
    }

    const scrollDistance = direction * track.clientWidth;
    track.scrollBy({ left: scrollDistance, behavior: 'smooth' });
  };

  const handleTrackKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const step = BUBBLE_STEP_BY_KEY[event.key];
    const target = event.target as HTMLElement;
    if (step === undefined || !target.matches(BUBBLE_FOCUS_SELECTOR)) {
      return;
    }

    const bubbles = Array.from(event.currentTarget.querySelectorAll<HTMLElement>(BUBBLE_FOCUS_SELECTOR));
    const nextBubble = bubbles[bubbles.indexOf(target) + step];
    // At either end the key falls through to the browser's own navigation.
    if (!nextBubble) {
      return;
    }

    event.preventDefault();
    // focus() alone leaves a partly visible bubble clipped at the track edge.
    nextBubble.focus({ preventScroll: true });
    nextBubble.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  };

  const openEventData = useMemo(() => {
    return openEvent ? seasonEvents[openEvent] : undefined;
  }, [seasonEvents, openEvent]);

  return (
    <AvatarCarouselPresentation
      sortedEvents={sortedEvents}
      openEvent={openEvent}
      openEventData={openEventData}
      seasonContext={seasonContext}
      trackRef={trackRef}
      onTrackScroll={syncScrollEdges}
      onTrackKeyDown={handleTrackKeyDown}
      onAvatarClick={onAvatarClick}
      onPrev={() => scrollByVisibleWidth(-1)}
      onNext={() => scrollByVisibleWidth(1)}
      canGoPrev={scrollEdges.canGoPrev}
      canGoNext={scrollEdges.canGoNext}
    />
  );
};

export default AvatarCarouselContainer;
